import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Payment as PaymentIcon } from '@mui/icons-material';
import api from '../services/api';
import { formatPricePerGallon } from '../utils/pricing';

function formatCurrency(value) {
  const n = Number(value) || 0;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatTaxPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n}%`;
}

function formatFuelLine(jobLine) {
  const parts = [];
  const pricing = jobLine.fuel_pricing;
  if (jobLine.diesel_gallons > 0) {
    let diesel = `${jobLine.diesel_gallons} gal Diesel`;
    if (pricing?.diesel_price_per_gallon != null) {
      diesel += ` @ $${formatPricePerGallon(pricing.diesel_price_per_gallon)}/gal`;
    }
    parts.push(diesel);
  }
  if (jobLine.def_gallons > 0) {
    let defLine = `${jobLine.def_gallons} gal DEF`;
    if (pricing?.def_price_per_gallon != null) {
      defLine += ` @ $${formatPricePerGallon(pricing.def_price_per_gallon)}/gal`;
    }
    parts.push(defLine);
  }
  return parts.length ? parts.join(' / ') : '—';
}

function formatPricingSnapshot(snapshot) {
  if (!snapshot) return null;
  return (
    <>
      Diesel ${formatPricePerGallon(snapshot.diesel_price_per_gallon)}/gal (
      {formatTaxPercent(snapshot.diesel_tax_percent)} tax) · DEF $
      {formatPricePerGallon(snapshot.def_price_per_gallon)}/gal (
      {formatTaxPercent(snapshot.def_tax_percent)} tax)
    </>
  );
}

function lineItemTotal(line) {
  if (line?.line_total != null) return Number(line.line_total) || 0;
  const delivery = Number(line?.delivery_fee) || 0;
  if (line?.fuel_cost != null) return delivery + (Number(line.fuel_cost) || 0);
  const pricing = line?.fuel_pricing;
  let fuel = 0;
  if (pricing) {
    fuel +=
      (Number(line.diesel_gallons) || 0) * (Number(pricing.diesel_price_per_gallon) || 0);
    fuel += (Number(line.def_gallons) || 0) * (Number(pricing.def_price_per_gallon) || 0);
  }
  return delivery + fuel;
}

function getStatusColor(status) {
  const colors = {
    submitted: 'warning',
    paid: 'success',
    partial: 'info',
    overdue: 'error',
    pending: 'default',
    cancelled: 'default',
  };
  return colors[status] || 'default';
}

async function downloadInvoicePdf(invoice) {
  if (!invoice?.id) return;
  const response = await api.get(`/invoices/${invoice.id}/download_pdf/`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${invoice.invoice_number || invoice.id}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default function InvoiceDetailsDialog({
  open,
  onClose,
  invoiceId = null,
  invoice: initialInvoice = null,
  showCustomer = false,
  showPublicPayLink = false,
  onPay = null,
  isMobile = false,
}) {
  const [invoice, setInvoice] = useState(initialInvoice);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) {
      setError('');
      return;
    }

    const id = invoiceId || initialInvoice?.id;
    if (!id) {
      setInvoice(initialInvoice);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/invoices/${id}/`);
        if (!cancelled) setInvoice(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              err.response?.data?.detail ||
              'Failed to load invoice details.'
          );
          setInvoice(initialInvoice);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, invoiceId, initialInvoice]);

  const handleDownload = async () => {
    if (!invoice) return;
    setDownloading(true);
    try {
      await downloadInvoicePdf(invoice);
    } catch (err) {
      console.error('Error downloading invoice PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const jobLines = invoice?.jobs || [];
  const fuelPricing = invoice?.fuel_pricing || [];
  const subtotal = Number(invoice?.amount) || 0;
  const tax = Number(invoice?.tax) || 0;
  const surchargePercent = Number(invoice?.credit_card_surcharge_percent) || 0;
  const surchargeAmount = Number(invoice?.credit_card_surcharge_amount) || 0;
  const beforeSurcharge = subtotal + tax;
  const hasSurchargeInfo =
    invoice?.credit_card_surcharge_percent != null ||
    invoice?.credit_card_surcharge_amount != null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{ '& .MuiDialog-paper': { m: { xs: 0, sm: 2 } } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="span">
            Invoice {invoice?.invoice_number || ''}
          </Typography>
          {invoice?.status && (
            <Chip label={invoice.status} color={getStatusColor(invoice.status)} size="small" />
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {invoice && (
              <>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Summary
                </Typography>
                {showCustomer && invoice.customer_name && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Customer:</strong> {invoice.customer_name}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Created:</strong> {formatDateTime(invoice.created_at)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Due date:</strong> {formatDate(invoice.due_date)}
                </Typography>
                {invoice.paid_at && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Paid:</strong> {formatDateTime(invoice.paid_at)}
                  </Typography>
                )}

                <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Subtotal (fuel + delivery):</strong> {formatCurrency(subtotal)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Tax:</strong> {formatCurrency(tax)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Before card surcharge:</strong> {formatCurrency(beforeSurcharge)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Credit card surcharge ({formatTaxPercent(surchargePercent)}):</strong>{' '}
                    {formatCurrency(surchargeAmount)}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
                    Total: {formatCurrency(invoice.total_amount)}
                  </Typography>
                </Box>

                {hasSurchargeInfo && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Credit card surcharge
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Rate:</strong> {formatTaxPercent(surchargePercent)} of subtotal + tax
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Base amount:</strong> {formatCurrency(beforeSurcharge)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Surcharge:</strong> {formatCurrency(surchargeAmount)}
                    </Typography>
                    {surchargePercent > 0 && surchargeAmount > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatCurrency(beforeSurcharge)} × {formatTaxPercent(surchargePercent)} ={' '}
                        {formatCurrency(surchargeAmount)}
                      </Typography>
                    )}
                  </Box>
                )}

                {fuelPricing.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Fuel pricing used
                    </Typography>
                    {fuelPricing.map((snapshot) => (
                      <Typography key={snapshot.pricing_date} variant="body2" sx={{ mb: 0.5 }}>
                        {fuelPricing.length > 1 && (
                          <>
                            <strong>{formatDate(snapshot.pricing_date)}:</strong>{' '}
                          </>
                        )}
                        {formatPricingSnapshot(snapshot)}
                      </Typography>
                    ))}
                  </Box>
                )}

                {showPublicPayLink && invoice.public_pay_url && (
                  <Typography variant="body2" sx={{ mb: 2, wordBreak: 'break-all' }}>
                    <strong>Public pay link:</strong>{' '}
                    <Link href={invoice.public_pay_url} target="_blank" rel="noopener noreferrer">
                      {invoice.public_pay_url}
                    </Link>
                  </Typography>
                )}

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Line items ({jobLines.length})
                </Typography>
                {jobLines.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No jobs on this invoice.
                  </Typography>
                ) : (
                  <TableContainer sx={{ mb: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Location</TableCell>
                          <TableCell>Fuel</TableCell>
                          <TableCell>Delivery notes</TableCell>
                          <TableCell align="right">Delivery fee</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobLines.map((line) => (
                          <TableRow key={line.id || line.job_id}>
                            <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-wrap' }}>
                              {line.job_location || '—'}
                            </TableCell>
                            <TableCell>{formatFuelLine(line)}</TableCell>
                            <TableCell sx={{ maxWidth: 200, whiteSpace: 'pre-wrap' }}>
                              {line.delivery_notes?.trim() ? line.delivery_notes : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(line.delivery_fee)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {formatCurrency(lineItemTotal(line))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {invoice?.pdf_file_url && (
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={downloading}
          >
            Download PDF
          </Button>
        )}
        {onPay && invoice?.status !== 'paid' && (
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            onClick={() => onPay(invoice)}
          >
            Pay
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
